/**
 * KEIKKA Live Map Page (Unified Map Engine)
 * 
 * Supports multiple layers: services, jobs, accommodation, rentals, auto
 * Route: /map?layer=services
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents, Polyline, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import axios from 'axios';
import { useLanguage } from '@/lib/context';
import { usePrefs } from '@/lib/usePrefs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  MapPin,
  Star,
  Phone,
  ExternalLink,
  Filter,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Zap,
  Shield,
  Award,
  Share2,
  Copy,
  Check,
  Wrench,
  Briefcase,
  Home,
  Key,
  Car,
  Navigation,
  Plus,
  Building2,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import GlobalNav from '@/components/GlobalNav';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { MapUpsellBanner } from '@/components/MapUpsellBanner';
import { trackMapEvent } from '@/lib/mapAnalytics';
import { ClaimReportLinks } from '@/components/ClaimReportLinks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Layer configuration - includes businesses layer
const LAYER_CONFIG = {
  services: { icon: Wrench, color: '#F97316', name: 'Services' },
  businesses: { icon: Building2, color: '#6366F1', name: 'Businesses' },
  jobs: { icon: Briefcase, color: '#3B82F6', name: 'Jobs' },
  accommodation: { icon: Home, color: '#22C55E', name: 'Accommodation' },
  rentals: { icon: Key, color: '#A855F7', name: 'Rentals' },
  auto: { icon: Car, color: '#EF4444', name: 'Auto' }
};

// Static city coordinate table for logistics route-preview (mirrors
// /pages/LogisticsPage.jsx CITIES). Single source of truth at MVP scale.
const ROUTE_CITY_COORDS = {
  NO: {
    Oslo: [59.9139, 10.7522], Bergen: [60.3913, 5.3221],
    Trondheim: [63.4305, 10.3951], Stavanger: [58.9700, 5.7331],
    Drammen: [59.7440, 10.2045],
  },
  SE: {
    Stockholm: [59.3293, 18.0686], 'Göteborg': [57.7089, 11.9746],
    'Malmö': [55.6050, 13.0038], Uppsala: [59.8586, 17.6389],
  },
  FI: {
    Helsinki: [60.1699, 24.9384], Espoo: [60.2055, 24.6559],
    Tampere: [61.4978, 23.7610], Turku: [60.4518, 22.2666],
    Oulu: [65.0121, 25.4651],
  },
  EE: {
    Tallinn: [59.4204, 24.7470], Tartu: [58.3776, 26.7290],
    'Pärnu': [58.3859, 24.4971], Narva: [59.3793, 28.1791],
  },
};

// Country fallback centers when a city is unknown.
const ROUTE_COUNTRY_CENTERS = {
  NO: [60.5, 8.5], SE: [62.0, 15.0], FI: [62.5, 25.5], EE: [58.7, 25.5],
};

const haversineKm = (a, b) => {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(x));
};

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createCustomIcon = (color, isFeatured = false, isUnclaimed = false) => {
  const size = isFeatured ? 40 : 32;
  // Unclaimed businesses get gray color with dashed border
  const bgColor = isUnclaimed ? '#9CA3AF' : color;
  const borderStyle = isUnclaimed ? 'border: 3px dashed #6B7280;' : 'border: 3px solid white;';
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="marker-pin" style="
        width: ${size}px;
        height: ${size}px;
        background: ${bgColor};
        ${borderStyle}
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        cursor: pointer;
        pointer-events: auto;
        ${isFeatured ? 'animation: pulse 2s infinite;' : ''}
        ${isUnclaimed ? 'opacity: 0.8;' : ''}
      ">
        ${isFeatured ? '<div style="position:absolute;top:-8px;left:50%;transform:translateX(-50%) rotate(45deg);font-size:12px;">⭐</div>' : ''}
        ${isUnclaimed ? '<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(45deg);font-size:10px;color:white;">?</div>' : ''}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size]
  });
};

const TIER_COLORS = {
  premium: '#FFD700',
  boost: '#22C55E',
  basic: '#3B82F6'
};

// Generate stable jittered coordinates for privacy (BASIC/hidden providers)
// Uses provider ID and current date for stability within same day
const getJitteredCoordinates = (lat, lng, providerId) => {
  // Create a stable seed based on provider ID and current date
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const seed = `${providerId}-${today}`;
  
  // Simple hash function for stable randomness
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Convert hash to offset in range [-500m, +500m]
  // ~0.0045 degrees ≈ 500m at equator
  const maxOffset = 0.0045;
  const latOffset = ((hash % 1000) / 1000) * maxOffset * 2 - maxOffset;
  const lngOffset = (((hash >> 10) % 1000) / 1000) * maxOffset * 2 - maxOffset;
  
  return {
    lat: lat + latOffset,
    lng: lng + lngOffset
  };
};

// Check if provider has paid/visible exact location
const canShowExactLocation = (item) => {
  // BOOST tier or higher gets exact coordinates
  // BASIC with map_visible still gets jittered
  return item.tier === 'boost' || item.tier === 'premium';
};

// Detect if user is on iOS/macOS for Apple Maps fallback
const isAppleDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor || '';
  return /iPad|iPhone|iPod|Macintosh/.test(ua) && !window.MSStream;
};

// Generate navigation URL - Apple Maps for iOS/Mac, Google Maps for others
const getNavigationUrl = (lat, lng) => {
  if (isAppleDevice()) {
    return `https://maps.apple.com/?daddr=${lat},${lng}`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
};

// Generate maps view URL - Apple Maps for iOS/Mac, Google Maps for others
const getGoogleMapsUrl = (lat, lng) => {
  if (isAppleDevice()) {
    return `https://maps.apple.com/?ll=${lat},${lng}&q=${lat},${lng}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
};

// Get the appropriate maps label based on device
const getMapsLabel = (t, isApple) => {
  if (isApple) {
    return 'Apple Maps';
  }
  return t('map.openInGoogleMaps') || 'Maps';
};

// MarkerClusterGroup component for clustering
function MarkerClusterGroup({ items, onMarkerClick, t, layerColor, activeLayer }) {
  const map = useMap();
  const clusterGroupRef = useRef(null);
  
  // Stabilise callback refs so the effect does NOT rebuild the cluster
  // group on every parent re-render (the rebuild destroys the popup the
  // user just opened — classic flicker / "popup never shows" bug).
  const onMarkerClickRef = useRef(onMarkerClick);
  useEffect(() => {
    onMarkerClickRef.current = onMarkerClick;
  }, [onMarkerClick]);
  
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  
  // Build a stable signature of the items so reference-only changes
  // (same contents, new array) don't trigger a rebuild.
  const itemsSignature = useMemo(
    () => (items || []).map((i) => `${i.id}:${i.lat},${i.lng}:${i.status || i.tier || ''}`).join('|'),
    [items]
  );
  
  useEffect(() => {
    if (!map) return;
    
    // Remove existing cluster group
    if (clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current);
    }
    
    // Create new cluster group with custom styling
    const clusterGroup = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      disableClusteringAtZoom: 16,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        let size = 'small';
        let className = 'marker-cluster-small';
        
        if (count >= 100) {
          size = 'large';
          className = 'marker-cluster-large';
        } else if (count >= 10) {
          size = 'medium';
          className = 'marker-cluster-medium';
        }
        
        return L.divIcon({
          html: `<div class="cluster-inner"><span>${count}</span></div>`,
          className: `custom-cluster ${className}`,
          iconSize: L.point(40, 40)
        });
      }
    });
    
    // Add markers to cluster group
    items.forEach(item => {
      // Check if this is a business from catalog (has 'status' field)
      const isBusinessLayer = activeLayer === 'businesses';
      const isUnclaimed = item.status === 'UNCLAIMED';
      const isClaimed = item.status === 'CLAIMED';
      
      // Determine marker color based on layer and status
      let markerColor = layerColor || TIER_COLORS.basic;
      if (isBusinessLayer) {
        markerColor = isClaimed ? '#22C55E' : '#6366F1'; // Green for claimed, indigo for unclaimed
      } else {
        markerColor = TIER_COLORS[item.tier] || layerColor || TIER_COLORS.basic;
      }
      
      const icon = createCustomIcon(
        markerColor,
        item.is_featured || (isClaimed && item.is_verified),
        isUnclaimed
      );
      
      const marker = L.marker([item.lat, item.lng], { icon });
      // Stash the item reference on the marker so the cluster-level click
      // handler can route to the sidebar selector.
      marker._item = item;
      
      // Determine coordinates for navigation (exact or jittered based on tier/status)
      const useExactLocation = isBusinessLayer 
        ? isClaimed 
        : canShowExactLocation(item);
      const navCoords = useExactLocation 
        ? { lat: item.lat, lng: item.lng }
        : getJitteredCoordinates(item.lat, item.lng, item.id);
      
      // Get URLs - Apple Maps for iOS/Mac, Google Maps for others
      const isApple = isAppleDevice();
      const navigateUrl = getNavigationUrl(navCoords.lat, navCoords.lng);
      const mapsUrl = getGoogleMapsUrl(navCoords.lat, navCoords.lng);
      const mapsLabel = isApple ? 'Apple Maps' : (t('map.openInGoogleMaps') || 'Maps');
      
      // Build status badge HTML
      let statusBadge = '';
      if (isBusinessLayer) {
        if (isClaimed) {
          statusBadge = `<span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700 ml-2">
            <svg class="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
            ${t('businessCatalog.claimed') || 'Kinnitatud'}
          </span>`;
        } else {
          statusBadge = `<span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 ml-2">
            <svg class="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
            ${t('businessCatalog.unclaimed') || 'Kinnitamata'}
          </span>`;
        }
      }
      
      // Build CTA button based on status
      let ctaButton = '';
      if (isBusinessLayer && isUnclaimed) {
        ctaButton = `
          <a href="/business/${item.id}?claim=true" 
             class="block w-full text-center text-xs bg-orange-500 text-white py-1.5 px-3 rounded hover:bg-orange-600 transition mb-2"
             data-testid="popup-claim-btn">
            ${t('businessCatalog.claimBusiness') || 'Kinnita see ettevõte'}
          </a>
        `;
      }
      
      // Create popup content with navigation buttons
      const detailsUrl = isBusinessLayer ? `/business/${item.id}` : item.url;
      const demoBadge = item.is_demo
        ? `<span class="inline-flex items-center text-[10px] uppercase tracking-wide font-bold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded mr-1" data-testid="popup-demo-badge">DEMO</span>`
        : '';
      const demoNote = item.is_demo
        ? `<p class="text-[11px] text-purple-700 italic mb-2" data-testid="popup-demo-note">${t('map.demoExampleProvider') || 'Example provider — shown only in demo mode.'}</p>`
        : '';
      const popupContent = `
        <div class="p-2 min-w-[220px]" data-provider-id="${item.id}" data-provider-tier="${item.tier || ''}" data-provider-country="${item.country}" data-provider-category="${item.subtypes?.[0] || item.categories?.[0] || ''}" data-is-demo="${item.is_demo ? '1' : '0'}">
          <div class="flex items-center gap-1 mb-2 flex-wrap">
            ${demoBadge}
            <h3 class="font-semibold text-sm">${item.title || item.name}</h3>
            ${item.is_featured ? '<span class="text-yellow-500">★</span>' : ''}
            ${statusBadge}
          </div>
          ${demoNote}
          <p class="text-xs text-gray-500 mb-2">${item.city || ''}, ${item.country || item.country_code || ''}</p>
          ${item.rating ? `<div class="text-xs mb-2">★ ${item.rating.toFixed(1)}</div>` : ''}
          
          <div class="space-y-2">
            ${ctaButton}
            <a href="${detailsUrl}"
               class="kk-popup-btn ${isUnclaimed ? 'kk-popup-btn--primary-muted' : 'kk-popup-btn--primary'}"
               data-testid="popup-view-details">
              ${t('map.viewDetails')}
            </a>

            <div class="flex flex-col gap-1.5">
              <a href="${navigateUrl}"
                 target="_blank"
                 rel="noopener noreferrer"
                 class="kk-popup-btn kk-popup-btn--secondary map-navigate-btn"
                 data-testid="popup-navigate-btn"
                 data-provider-id="${item.id}"
                 data-provider-tier="${item.tier || ''}"
                 data-provider-country="${item.country || item.country_code}"
                 data-provider-category="${item.subtypes?.[0] || item.categories?.[0] || ''}">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                ${t('map.navigateTo') || 'Sõida siia'}
              </a>
              <a href="${mapsUrl}"
                 target="_blank"
                 rel="noopener noreferrer"
                 class="kk-popup-btn kk-popup-btn--tertiary map-googlemaps-btn"
                 data-testid="popup-googlemaps-btn"
                 data-provider-id="${item.id}"
                 data-provider-tier="${item.tier || ''}"
                 data-provider-country="${item.country || item.country_code}"
                 data-provider-category="${item.subtypes?.[0] || item.categories?.[0] || ''}">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg>
                ${mapsLabel}
              </a>
            </div>
          </div>
        </div>
      `;
      
      marker.bindPopup(popupContent, {
        maxWidth: 280,
        minWidth: 220,
        // autoPan causes the map to pan to keep popup visible, but that
        // fires a `moveend` event on the map which, in this component,
        // triggers a re-fetch of items and a full cluster-group rebuild —
        // the rebuild destroys the marker before the popup can render.
        // Disable autoPan to break this feedback loop.
        autoPan: false,
        closeOnClick: false
      });
      
      // Marker click: open popup and notify sidebar via stable ref.
      marker.on('click', () => {
        marker.openPopup();
        if (onMarkerClickRef.current) {
          onMarkerClickRef.current(item);
        }
      });
      
      clusterGroup.addLayer(marker);
    });
    
    // Cluster-level click handler as a safety net.
    clusterGroup.on('click', (e) => {
      const clicked = e.layer;
      if (!clicked) return;
      if (typeof clicked.openPopup === 'function') {
        clicked.openPopup();
      }
      if (onMarkerClickRef.current && clicked._item) {
        onMarkerClickRef.current(clicked._item);
      }
    });
    
    map.addLayer(clusterGroup);
    clusterGroupRef.current = clusterGroup;
    
    return () => {
      if (clusterGroupRef.current) {
        map.removeLayer(clusterGroupRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, itemsSignature, layerColor, activeLayer, t]);
  
  return null;
}

// Map event handler component
function MapEventHandler({ onBoundsChange }) {
  const map = useMapEvents({
    moveend: () => {
      const bounds = map.getBounds();
      const center = map.getCenter();
      onBoundsChange({
        bbox: `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`,
        center: { lat: center.lat, lng: center.lng },
        zoom: map.getZoom()
      });
    }
  });
  return null;
}

/**
 * PopupClickTracker — attaches a single delegated click listener to the
 * Leaflet map container that catches taps on `.map-navigate-btn` and
 * `.map-googlemaps-btn` inside dynamically-rendered popup HTML and fires
 * the clean funnel events nav_click / maps_click. Dedupe is handled in
 * trackMapEvent.
 */
function PopupClickTracker() {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const container = map.getContainer();
    const onClick = (e) => {
      const navBtn = e.target.closest('.map-navigate-btn');
      const mapsBtn = e.target.closest('.map-googlemaps-btn');
      const btn = navBtn || mapsBtn;
      if (!btn) return;
      const eventType = navBtn ? 'nav_click' : 'maps_click';
      const label = btn.textContent?.trim().replace(/\s+/g, ' ').slice(0, 60) || eventType;
      const providerId = btn.dataset.providerId || undefined;
      trackMapEvent(eventType, { label, providerId });
    };
    container.addEventListener('click', onClick);
    return () => {
      container.removeEventListener('click', onClick);
    };
  }, [map]);
  return null;
}

// Component to set map view
function SetViewOnChange({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView([center.lat, center.lng], zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  return null;
}

// Empty State with CTA Component
function EmptyStateWithCTA({ activeLayer, availableLayers, layerColor, LayerIcon, t, language }) {
  const layer = availableLayers.find(l => l.id === activeLayer);
  const addCtaRoute = layer?.add_cta_route;
  
  // i18n labels for empty state CTAs
  const CTA_LABELS = {
    services: {
      en: 'Add Your Service',
      et: 'Lisa teenus',
      fi: 'Lisää palvelu'
    },
    jobs: {
      en: 'Post a Job',
      et: 'Lisa töökuulutus',
      fi: 'Lisää työilmoitus'
    },
    accommodation: {
      en: 'Offer Accommodation',
      et: 'Paku majutust',
      fi: 'Tarjoa majoitusta'
    },
    rentals: {
      en: 'List Your Rental',
      et: 'Lisa rendipakkumine',
      fi: 'Lisää vuokrausilmoitus'
    },
    auto: {
      en: 'Add Auto Service',
      et: 'Lisa autoteenus',
      fi: 'Lisää autopalvelu'
    }
  };
  
  const EMPTY_LABELS = {
    services: {
      en: 'No services in this area yet',
      et: 'Selles piirkonnas teenuseid veel pole',
      fi: 'Tällä alueella ei ole vielä palveluita'
    },
    jobs: {
      en: 'No jobs posted in this area',
      et: 'Selles piirkonnas töökuulutusi pole',
      fi: 'Tällä alueella ei ole työpaikkoja'
    },
    accommodation: {
      en: 'No accommodation listed here',
      et: 'Siin majutust pole',
      fi: 'Täällä ei ole majoitustarjontaa'
    },
    rentals: {
      en: 'No rentals available',
      et: 'Rendipakkumisi pole',
      fi: 'Vuokrauksesi ei saatavilla'
    },
    auto: {
      en: 'No auto services here',
      et: 'Autoteenuseid pole',
      fi: 'Autopalveluita ei löydy'
    }
  };
  
  const lang = language || 'en';
  const ctaLabel = CTA_LABELS[activeLayer]?.[lang] || CTA_LABELS[activeLayer]?.en || 'Add';
  const emptyLabel = EMPTY_LABELS[activeLayer]?.[lang] || EMPTY_LABELS[activeLayer]?.en || 'No results';
  
  return (
    <div className="text-center py-8 text-muted-foreground">
      <LayerIcon className="w-12 h-12 mx-auto mb-4 opacity-50" style={{ color: layerColor }} />
      <p className="font-medium mb-2">{emptyLabel}</p>
      <p className="text-sm mb-4">{t('map.tryDifferentFilters') || 'Try different filters or zoom out'}</p>
      
      {addCtaRoute && (
        <Button asChild className="mt-2" style={{ backgroundColor: layerColor }}>
          <Link to={addCtaRoute} data-testid={`add-${activeLayer}-cta`}>
            <Plus className="w-4 h-4 mr-2" />
            {ctaLabel}
          </Link>
        </Button>
      )}
    </div>
  );
}

// Generic Item Card Component (works for all layers)
function ItemCard({ item, isSelected, onClick, t, layerColor }) {
  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-md ${
        isSelected ? 'ring-2 ring-brand shadow-lg' : ''
      }`}
      onClick={onClick}
      data-testid={`item-card-${item.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold truncate">{item.title}</h3>
              {item.is_featured && (
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 text-xs">
                  <Zap className="w-3 h-3 mr-1" />
                  {t('map.featured')}
                </Badge>
              )}
              {item.is_verified && (
                <Shield className="w-4 h-4 text-green-600" />
              )}
            </div>
            
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <MapPin className="w-3 h-3" style={{ color: layerColor }} />
              <span>{item.city}, {item.country}</span>
            </div>
            
            <div className="flex flex-wrap gap-1 mb-2">
              {item.subtypes?.slice(0, 2).map((cat, i) => (
                <Badge key={i} variant="outline" className="text-xs">
                  {t(`categories.${cat}`) || cat}
                </Badge>
              ))}
            </div>
            
            {item.rating && (
              <div className="flex items-center gap-1 text-sm">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span className="font-medium">{item.rating.toFixed(1)}</span>
                {item.review_count && (
                  <span className="text-muted-foreground">({item.review_count})</span>
                )}
              </div>
            )}
            
            {item.price_from && (
              <div className="text-sm text-muted-foreground mt-1">
                {t('map.priceFrom')} €{item.price_from}
              </div>
            )}
          </div>
        </div>
        
        <div className="mt-3 flex gap-2">
          <Button 
            size="sm" 
            className="flex-1"
            asChild
          >
            <Link to={item.url}>
              {t('map.viewDetails') || 'View Details'}
              <ExternalLink className="w-3 h-3 ml-1" />
            </Link>
          </Button>
        </div>

        {/* Claim / Report — public feeders for the admin queues */}
        <div className="mt-3 pt-2 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
          <ClaimReportLinks business={item} />
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- Logistics Route Preview (MVP) ----------
// Inner Leaflet primitives — must be a child of <MapContainer>.
// Reads ?mode=logistics&originCountry=NO&originCity=Oslo&destCountry=NO&destCity=Bergen
// from the URL, geocodes via the static ROUTE_CITY_COORDS table, then either
// fetches a road-following polyline from OSRM (truck-friendly = "driving"
// profile preferring highways) or falls back to a great-circle straight line
// labelled "Eelvaade (MVP)".
const LogisticsRouteLayer = ({ params, onRouteUpdate }) => {
  const map = useMap();
  const [route, setRoute] = useState({ coords: null, source: 'pending', km: 0, etaMin: 0 });

  const originCountry = params.get('originCountry');
  const originCity = params.get('originCity');
  const destCountry = params.get('destCountry');
  const destCity = params.get('destCity');

  const origin = useMemo(() => {
    if (!originCountry) return null;
    const c = ROUTE_CITY_COORDS[originCountry] || {};
    return c[originCity] || ROUTE_COUNTRY_CENTERS[originCountry] || null;
  }, [originCountry, originCity]);

  const dest = useMemo(() => {
    if (!destCountry) return null;
    const c = ROUTE_CITY_COORDS[destCountry] || {};
    return c[destCity] || ROUTE_COUNTRY_CENTERS[destCountry] || null;
  }, [destCountry, destCity]);

  // Fit map bounds when origin/dest both available — fire immediately AND
  // again at +400ms to win against any concurrent setView from
  // SetViewOnChange running on initial mount.
  useEffect(() => {
    if (origin && dest && map) {
      const bounds = L.latLngBounds([origin, dest]);
      const fit = () => map.fitBounds(bounds, { padding: [60, 60], animate: false });
      fit();
      const t1 = setTimeout(fit, 400);
      const t2 = setTimeout(fit, 1200);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [origin, dest, map]);

  // Fetch routing polyline. Truck-friendly preference order:
  //  1) OpenRouteService driving-hgv (real HGV restrictions) when
  //     REACT_APP_ORS_API_KEY is present
  //  2) OSRM public driving profile (highway-preferring fallback)
  //  3) Great-circle straight line (last-resort, labelled MVP)
  useEffect(() => {
    let cancelled = false;
    if (!origin || !dest) return;
    const km = haversineKm(origin, dest);
    const fallback = { coords: [origin, dest], source: 'fallback', km, etaMin: Math.round((km / 70) * 60) };
    const orsKey = process.env.REACT_APP_ORS_API_KEY;
    (async () => {
      // Step 1: ORS driving-hgv
      if (orsKey) {
        try {
          const res = await fetch(
            'https://api.openrouteservice.org/v2/directions/driving-hgv/geojson',
            {
              method: 'POST',
              headers: {
                Authorization: orsKey,
                'Content-Type': 'application/json',
                Accept: 'application/geo+json',
              },
              body: JSON.stringify({
                coordinates: [
                  [origin[1], origin[0]],
                  [dest[1], dest[0]],
                ],
                preference: 'recommended',
                instructions: false,
              }),
              signal: (() => {
                const c = new AbortController();
                setTimeout(() => c.abort(), 7000);
                return c.signal;
              })(),
            }
          );
          if (res.ok) {
            const data = await res.json();
            const f = data?.features?.[0];
            if (f?.geometry?.coordinates && f?.properties?.summary) {
              const coords = f.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
              const realKm = (f.properties.summary.distance || 0) / 1000;
              const realMin = Math.round((f.properties.summary.duration || 0) / 60);
              if (!cancelled) {
                const next = { coords, source: 'ors_hgv', km: realKm, etaMin: realMin };
                setRoute(next);
                onRouteUpdate?.(next, { originCity, destCity });
              }
              return;
            }
          }
        } catch (_e) { /* fall through */ }
      }
      // Step 2: OSRM driving
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${origin[1]},${origin[0]};${dest[1]},${dest[0]}?overview=full&geometries=geojson`;
        const ctrl = new AbortController();
        const tid = setTimeout(() => ctrl.abort(), 6000);
        const res = await fetch(url, { signal: ctrl.signal });
        clearTimeout(tid);
        if (!res.ok) throw new Error('osrm');
        const data = await res.json();
        const r = data?.routes?.[0];
        if (!r) throw new Error('no route');
        const coords = r.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
        const realKm = r.distance / 1000;
        const realMin = Math.round(r.duration / 60);
        if (!cancelled) {
          const next = { coords, source: 'osrm', km: realKm, etaMin: realMin };
          setRoute(next);
          onRouteUpdate?.(next, { originCity, destCity });
        }
      } catch (_e) {
        if (!cancelled) {
          setRoute(fallback);
          onRouteUpdate?.(fallback, { originCity, destCity });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [origin, dest, onRouteUpdate, originCity, destCity]);

  if (!origin || !dest) return null;

  return (
    <>
      {route.coords && (
        <Polyline
          positions={route.coords}
          pathOptions={{
            color:
              route.source === 'ors_hgv' ? '#15803d' :
              route.source === 'osrm' ? '#2563eb' : '#94a3b8',
            weight: 5,
            opacity: 0.85,
            dashArray: route.source === 'fallback' ? '8,8' : null,
          }}
        />
      )}
      <CircleMarker center={origin} radius={8} pathOptions={{ color: '#16a34a', fillColor: '#16a34a', fillOpacity: 1 }}>
        <Popup>{originCity || originCountry}</Popup>
      </CircleMarker>
      <CircleMarker center={dest} radius={8} pathOptions={{ color: '#dc2626', fillColor: '#dc2626', fillOpacity: 1 }}>
        <Popup>{destCity || destCountry}</Popup>
      </CircleMarker>
    </>
  );
};

// HTML overlay (sibling of MapContainer) — contains the route info card.
const LogisticsRouteInfoCard = ({ route, originCity, destCity, t }) => {
  if (!route || !route.coords) return null;
  const formatKm = (km) => `${km.toFixed(1)} km`;
  const formatEta = (min) => {
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  };
  return (
    <div
      className="absolute top-3 left-3 z-[1000] bg-white shadow-lg rounded-xl border border-slate-200 p-3 max-w-xs pointer-events-auto"
      data-testid="logistics-route-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
          route.source === 'ors_hgv' ? 'bg-emerald-700 text-white' : 'bg-slate-900 text-white'
        }`}>
          {route.source === 'ors_hgv'
            ? (t('logistics.routePreview.badgeHgv') || 'HGV truck')
            : t('logistics.routePreview.badge')}
        </span>
      </div>
      <div className="text-sm font-medium text-slate-900" data-testid="route-summary">
        {originCity} → {destCity}
      </div>
      <div className="text-xs text-slate-700 mt-1">
        <span className="font-semibold">{t('logistics.routePreview.distance')}:</span>{' '}
        <span data-testid="route-km">{formatKm(route.km)}</span>
        {' · '}
        <span className="font-semibold">{t('logistics.routePreview.eta')}:</span>{' '}
        <span data-testid="route-eta">{formatEta(route.etaMin)}</span>
      </div>
      {route.source === 'fallback' && (
        <div className="text-[11px] text-amber-700 mt-1" data-testid="route-fallback-note">
          {t('logistics.routePreview.fallback')}
        </div>
      )}
      <div className="text-[11px] text-slate-500 mt-2 leading-snug" data-testid="route-disclaimer">
        {t('logistics.routePreview.disclaimer')}
      </div>
    </div>
  );
};

// Main LiveMapPage Component
export default function LiveMapPage() {
  const { t, language } = useLanguage();
  const { prefs } = usePrefs();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Layer state
  const [activeLayer, setActiveLayer] = useState(() => {
    return searchParams.get('layer') || 'services';
  });
  const [availableLayers, setAvailableLayers] = useState([]);
  
  // State
  const [items, setItems] = useState([]);  // Renamed from providers to items (generic)
  const [subtypes, setSubtypes] = useState([]);  // Renamed from categories
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  // Route preview state for ?mode=logistics
  const [logisticsRoute, setLogisticsRoute] = useState(null);
  const handleLogisticsRouteUpdate = useCallback((r, ctx) => {
    setLogisticsRoute({ ...r, ...ctx });
  }, []);
  const [mapCenter, setMapCenter] = useState(() => {
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');
    if (lat && lng) {
      return { lat: parseFloat(lat), lng: parseFloat(lng) };
    }
    return { lat: 59.437, lng: 24.754 }; // Default: Tallinn
  });
  const [mapZoom, setMapZoom] = useState(() => {
    const zoom = searchParams.get('zoom');
    return zoom ? parseInt(zoom) : 8;
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Filters from URL, with fallback to user preferences (Seaded).
  const [filters, setFilters] = useState({
    subtype: searchParams.get('subtype') || searchParams.get('category') || '',
    country: searchParams.get('country') || prefs.serviceCountry || '',
    city: searchParams.get('city') || '',
    keyword: searchParams.get('q') || prefs.locationQuery || '',
    lat: searchParams.get('lat') ? parseFloat(searchParams.get('lat')) : null,
    lng: searchParams.get('lng') ? parseFloat(searchParams.get('lng')) : null,
    // Scalable location-scope system. 'country' is the global default
    // (no radius, no random local-only vibe). 'city' = nearby radius
    // around a chosen city. 'distance' = explicit km radius, up to 10 000.
    scopeMode: searchParams.get('scope') || 'country',
    radius_km: parseInt(searchParams.get('radius') || '50'),
    // City-mode area chip (default "Nearby" = 30 km, internal only).
    area_km: parseInt(searchParams.get('area') || '30'),
    cityLabel: searchParams.get('city') || '',
  });
  
  // Sort state - Nearest only for now
  // NOTE: "Rated" sort hidden until rating system is implemented
  const [sortBy, setSortBy] = useState('nearest');
  
  // Fetch available layers on mount
  useEffect(() => {
    const fetchLayers = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/map/layers?lang=${language}`);
        setAvailableLayers(res.data.layers || []);
      } catch (err) {
        console.error('Failed to fetch layers:', err);
      }
    };
    fetchLayers();
  }, [language]);
  
  // Fetch metadata when layer changes
  useEffect(() => {
    // Metadata is now fetched with items via unified search
    // Just trigger a refetch when layer changes
    fetchItems();
  }, [activeLayer]);
  
  // Fetch items when filters or layer changes
  const fetchItems = useCallback(async (bbox = null) => {
    setLoading(true);
    try {
      // Special handling for businesses layer - use Business Catalog API
      if (activeLayer === 'businesses') {
        const params = new URLSearchParams();
        
        // Businesses API requires bbox - generate from map center if not provided
        if (bbox) {
          params.set('bbox', bbox);
        } else {
          // Generate default bbox around map center (roughly 50km radius)
          const defaultBbox = `${mapCenter.lng - 0.5},${mapCenter.lat - 0.3},${mapCenter.lng + 0.5},${mapCenter.lat + 0.3}`;
          params.set('bbox', defaultBbox);
        }
        
        if (filters.country) params.set('country_code', filters.country);
        if (filters.city) params.set('city', filters.city);
        if (filters.keyword) params.set('q', filters.keyword);
        if (filters.subtype) params.set('categories', filters.subtype);
        params.set('limit', '200');
        
        const res = await axios.get(`${BACKEND_URL}/api/businesses/map/layer?${params.toString()}`);
        
        // Transform businesses to map items format
        const mappedItems = (res.data.items || []).map(b => ({
          id: b.id,
          name: b.name,
          title: b.name,
          lat: b.location?.lat || b.lat,
          lng: b.location?.lng || b.lng,
          city: b.city || b.region,
          country: b.country_code,
          country_code: b.country_code,
          status: b.status, // CLAIMED or UNCLAIMED
          is_verified: b.is_verified,
          is_featured: b.visibility_tier === 'BOOST' || b.is_verified,
          tier: b.status === 'CLAIMED' ? 'boost' : 'basic',
          categories: b.categories || [],
          subtypes: b.categories || [],
          phone: b.phone_public,
          website: b.website,
          url: `/business/${b.id}`
        }));
        
        setItems(mappedItems);
        
        // Set available categories for filter
        const categories = await axios.get(`${BACKEND_URL}/api/businesses/categories`);
        if (categories.data?.categories) {
          setSubtypes(categories.data.categories.map(c => ({
            id: c.id,
            name: c.name,
            count: c.count
          })));
        }
        
        // Set available countries for filter
        const countriesRes = await axios.get(`${BACKEND_URL}/api/businesses/countries`);
        if (countriesRes.data?.countries) {
          setCountries(countriesRes.data.countries.map(c => ({
            code: c.code,
            name: c.code,
            count: c.count
          })));
        }
        
        return;
      }
      
      // Standard layer handling (services, jobs, etc.)
      const params = new URLSearchParams();

      // Set layer
      params.set('layer', activeLayer);

      // Scope-aware geography params.
      //   scopeMode 'country'  -> send only country (NO radius / NO center)
      //   scopeMode 'city'     -> send center (lat/lng) + radius_km (default 30 = "Nearby")
      //   scopeMode 'distance' -> send center + the user-chosen radius_km (0..10000)
      //   bbox always wins when the user pans — keeps current behaviour.
      const scope = filters.scopeMode || 'country';
      if (bbox) {
        params.set('bbox', bbox);
      } else if (scope === 'country') {
        // Country-only mode: no radius, no center. Backend filters by country.
      } else if (scope === 'city') {
        if (filters.lat && filters.lng) {
          params.set('lat', filters.lat);
          params.set('lng', filters.lng);
          params.set('radius_km', String(filters.area_km || 30));
        }
      } else if (scope === 'distance') {
        if (filters.lat && filters.lng) {
          params.set('lat', filters.lat);
          params.set('lng', filters.lng);
          params.set('radius_km', String(filters.radius_km || 50));
        }
      }

      if (filters.subtype) params.set('subtype', filters.subtype);
      if (filters.country) params.set('country', filters.country);
      if (filters.city) params.set('city', filters.city);
      if (filters.keyword) params.set('q', filters.keyword);
      params.set('lang', language);
      params.set('limit', '200');
      // Always opt-in to empty-state fallback so a country with zero real
      // providers still shows demo pins (each clearly marked DEMO).
      params.set('fill_empty', '1');
      // Demo mode: include seeded demo providers (clearly flagged is_demo=true).
      // Activated by `?demo=1`/`?demo=true` in URL or by the DemoModeContext
      // localStorage flag set when the user came in via /demo?country=XX.
      try {
        const sp = new URLSearchParams(window.location.search);
        const fromUrl = sp.get('demo') === '1' || sp.get('demo') === 'true';
        const fromStorage = (() => {
          try { return localStorage.getItem('keikka_demo') === 'true'; } catch (_) { return false; }
        })();
        if (fromUrl || fromStorage) params.set('demo', '1');
      } catch (_) { /* SSR-safe */ }
      
      const res = await axios.get(`${BACKEND_URL}/api/map/search?${params.toString()}`);
      setItems(res.data.items || []);
      
      // Update subtypes and countries from response
      if (res.data.available_subtypes) {
        setSubtypes(res.data.available_subtypes);
      }
      if (res.data.available_countries) {
        setCountries(res.data.available_countries);
      }
      
      // Update map center if results have a center
      if (res.data.center && !filters.lat) {
        setMapCenter(res.data.center);
      }
    } catch (err) {
      console.error('Failed to fetch items:', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters, language, activeLayer, mapCenter]);
  
  // Initial fetch
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);
  
  // Handle map bounds change
  const handleBoundsChange = useCallback(({ bbox, center, zoom }) => {
    setMapZoom(zoom);
    // Only refetch if zoom is reasonable (not too zoomed out)
    if (zoom >= 6) {
      fetchItems(bbox);
    }
  }, [fetchItems]);
  
  // Update URL when filters or layer changes
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('layer', activeLayer);
    if (filters.subtype) params.set('subtype', filters.subtype);
    if (filters.country) params.set('country', filters.country);
    if (filters.city) params.set('city', filters.city);
    if (filters.keyword) params.set('q', filters.keyword);
    if (filters.lat) params.set('lat', filters.lat);
    if (filters.lng) params.set('lng', filters.lng);
    if (filters.radius_km !== 50) params.set('radius', filters.radius_km);

    // Preserve logistics route-preview params so this effect doesn't strip
    // them on first paint.
    for (const k of ['mode', 'originCountry', 'originCity', 'destCountry', 'destCity']) {
      const v = searchParams.get(k);
      if (v) params.set(k, v);
    }

    const newSearch = params.toString();
    if (newSearch !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [filters, activeLayer, searchParams, setSearchParams]);
  
  // Handle layer change
  const handleLayerChange = (newLayer) => {
    setActiveLayer(newLayer);
    // Reset subtype filter when changing layers (categories differ per layer)
    setFilters(prev => ({ ...prev, subtype: '' }));
  };
  
  // Update filter and trigger fetch
  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      subtype: '',
      country: '',
      city: '',
      keyword: '',
      lat: null,
      lng: null,
      radius_km: 50
    });
  };
  
  // Request user location
  const requestLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setFilters(prev => ({
            ...prev,
            lat: latitude,
            lng: longitude
          }));
          setMapCenter({ lat: latitude, lng: longitude });
          setMapZoom(12);
        },
        (err) => {
          console.warn('Geolocation error:', err);
        }
      );
    }
  };
  
  // Share map with current filters
  const handleShareMap = async () => {
    // Build share URL with all current filters and map state
    const shareParams = new URLSearchParams();
    
    shareParams.set('layer', activeLayer);
    if (filters.subtype) shareParams.set('subtype', filters.subtype);
    if (filters.country) shareParams.set('country', filters.country);
    if (filters.city) shareParams.set('city', filters.city);
    if (filters.keyword) shareParams.set('q', filters.keyword);
    if (mapCenter.lat && mapCenter.lng) {
      shareParams.set('lat', mapCenter.lat.toFixed(4));
      shareParams.set('lng', mapCenter.lng.toFixed(4));
    }
    if (mapZoom) shareParams.set('zoom', mapZoom.toString());
    shareParams.set('lang', language);
    
    const shareUrl = `${window.location.origin}/map${shareParams.toString() ? '?' + shareParams.toString() : ''}`;
    
    // Try native share first (mobile), fallback to clipboard
    if (navigator.share) {
      try {
        const layerName = LAYER_CONFIG[activeLayer]?.name || activeLayer;
        await navigator.share({
          title: `Keikka ${layerName} Map`,
          text: t('map.shareText') || 'Check out this on Keikka!',
          url: shareUrl
        });
        // Track share
        axios.post(`${BACKEND_URL}/api/map/analytics`, null, {
          params: { event_type: 'map_share', category: filters.subtype }
        }).catch(() => {});
      } catch (err) {
        if (err.name !== 'AbortError') {
          // User cancelled, fallback to clipboard
          copyToClipboard(shareUrl);
        }
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };
  
  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('map.linkCopied') || 'Link copied to clipboard!');
      // Track copy
      axios.post(`${BACKEND_URL}/api/map/analytics`, null, {
        params: { event_type: 'map_share_copy', category: filters.subtype }
      }).catch(() => {});
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error(t('map.copyFailed') || 'Failed to copy link');
    }
  };
  
  // Track analytics
  useEffect(() => {
    axios.post(`${BACKEND_URL}/api/map/analytics`, null, {
      params: {
        event_type: 'map_view',
        category: filters.subtype || undefined,
        country: filters.country || undefined
      }
    }).catch(() => {});
  }, []);
  
  // Analytics event delegation for popup buttons (navigate and google maps)
  useEffect(() => {
    const handlePopupClick = (e) => {
      const navigateBtn = e.target.closest('.map-navigate-btn');
      const googleMapsBtn = e.target.closest('.map-googlemaps-btn');
      
      if (navigateBtn) {
        const providerId = navigateBtn.dataset.providerId;
        const tier = navigateBtn.dataset.providerTier;
        const country = navigateBtn.dataset.providerCountry;
        const category = navigateBtn.dataset.providerCategory;
        
        axios.post(`${BACKEND_URL}/api/map/analytics`, null, {
          params: {
            event_type: 'map_navigate_click',
            provider_id: providerId,
            tier: tier,
            country: country,
            category: category
          }
        }).catch(() => {});
      }
      
      if (googleMapsBtn) {
        const providerId = googleMapsBtn.dataset.providerId;
        const tier = googleMapsBtn.dataset.providerTier;
        const country = googleMapsBtn.dataset.providerCountry;
        const category = googleMapsBtn.dataset.providerCategory;
        
        axios.post(`${BACKEND_URL}/api/map/analytics`, null, {
          params: {
            event_type: 'map_open_google_maps_click',
            provider_id: providerId,
            tier: tier,
            country: country,
            category: category
          }
        }).catch(() => {});
      }
    };
    
    // Add event listener to document for event delegation
    document.addEventListener('click', handlePopupClick);
    
    return () => {
      document.removeEventListener('click', handlePopupClick);
    };
  }, []);
  
  const hasActiveFilters = filters.subtype || filters.country || filters.city || filters.keyword;
  
  // Dynamic SEO content based on filters and layer
  const layerName = useMemo(() => {
    return LAYER_CONFIG[activeLayer]?.name || 'Services';
  }, [activeLayer]);
  
  const seoTitle = useMemo(() => {
    let title = `${layerName} Map - Keikka`;
    if (filters.subtype) {
      const catName = t(`categories.${filters.subtype}`) || filters.subtype;
      title = `${catName} - ${title}`;
    }
    if (filters.country) {
      title = `${title} in ${filters.country}`;
    }
    return title;
  }, [filters.subtype, filters.country, layerName, t]);
  
  const seoDescription = useMemo(() => {
    let desc = `Find ${layerName.toLowerCase()} on the map`;
    if (filters.subtype) {
      const catName = t(`categories.${filters.subtype}`) || filters.subtype;
      desc = `Find ${catName} near you`;
    }
    if (filters.country) {
      desc += ` in ${filters.country}`;
    }
    if (items.length > 0) {
      desc += `. ${items.length} results available.`;
    }
    return desc;
  }, [filters.subtype, filters.country, items.length, layerName, t]);
  
  const seoUrl = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('lang', language);
    return `${window.location.origin}/map${params.toString() ? '?' + params.toString() : ''}`;
  }, [language]);
  
  // Default OG image - static map preview
  const seoImage = `${window.location.origin}/og-map-preview.png`;
  
  // Get layer icon component
  const LayerIcon = LAYER_CONFIG[activeLayer]?.icon || Wrench;
  const layerColor = LAYER_CONFIG[activeLayer]?.color || '#F97316';
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead 
        title={seoTitle}
        description={seoDescription}
        url={seoUrl}
        image={seoImage}
        type="website"
      />
      
      <GlobalNav />
      
      <main className="flex-1 flex flex-col">
        {/* Layer Switcher */}
        <div className="border-b bg-card px-4 py-2">
          <Tabs value={activeLayer} onValueChange={handleLayerChange} className="w-full">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-5 h-auto">
              {Object.entries(LAYER_CONFIG).map(([layerId, config]) => {
                const IconComponent = config.icon;
                const layer = availableLayers.find(l => l.id === layerId);
                const isEnabled = layer?.enabled ?? (layerId === 'services');
                // Use translated name from backend, fallback to i18n key, then config.name
                const layerDisplayName = layer?.name || t(`map.layers.${layerId}`) || config.name;
                
                return (
                  <TabsTrigger
                    key={layerId}
                    value={layerId}
                    disabled={!isEnabled}
                    className="flex flex-col items-center gap-1 py-2 data-[state=active]:bg-background"
                    style={{ '--layer-color': config.color }}
                    data-testid={`layer-tab-${layerId}`}
                  >
                    <IconComponent 
                      className="w-4 h-4" 
                      style={{ color: activeLayer === layerId ? config.color : undefined }}
                    />
                    <span className="text-xs">{layerDisplayName}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
        </div>
        
        <div className="flex-1 flex flex-col lg:flex-row">
          {/* Sidebar - Filters & Results */}
          <aside 
            className={`
              ${sidebarOpen ? 'w-full lg:w-96' : 'w-0 lg:w-12'}
              flex-shrink-0 bg-card border-r transition-all duration-300 overflow-hidden
              ${sidebarOpen ? '' : 'lg:overflow-visible'}
            `}
          >
            {/* Toggle button */}
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-20 left-[calc(100%-2rem)] lg:left-auto lg:right-0 z-20 lg:translate-x-full bg-card border shadow-md"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
          
          {sidebarOpen && (
            <div className="h-full flex flex-col">
              {/* Filters */}
              <div className="p-4 border-b space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    {t('map.filters')}
                  </h2>
                  {hasActiveFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="w-4 h-4 mr-1" />
                      {t('map.clearFilters')}
                    </Button>
                  )}
                </div>
                
                {/* Keyword Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t('map.searchPlaceholder')}
                    value={filters.keyword}
                    onChange={(e) => updateFilter('keyword', e.target.value)}
                    className="pl-10"
                    data-testid="map-search-input"
                  />
                </div>
                
                {/* Category/Subtype Filter */}
                <Select
                  value={filters.subtype || "all"}
                  onValueChange={(v) => updateFilter('subtype', v === 'all' ? '' : v)}
                >
                  <SelectTrigger data-testid="subtype-filter">
                    <SelectValue placeholder={t('map.allCategories')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('map.allCategories')}</SelectItem>
                    {subtypes.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {t(`categories.${cat.id}`) || cat.name} ({cat.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Country Filter */}
                <Select
                  value={filters.country || "all"}
                  onValueChange={(v) => {
                    const newValue = v === 'all' ? '' : v;
                    updateFilter('country', newValue);
                    // Center map on country
                    if (newValue) {
                      const country = countries.find(c => c.code === newValue);
                      if (country?.center) {
                        setMapCenter(country.center);
                        setMapZoom(7);
                      }
                    }
                  }}
                >
                  <SelectTrigger data-testid="country-filter">
                    <SelectValue placeholder={t('map.allCountries')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('map.allCountries')}</SelectItem>
                    {countries.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {t(`countries.${c.code}`, { defaultValue: c.name })} ({c.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* ---- LOCATION SCOPE (3 modes) ---- */}
                <div data-testid="scope-section" className="space-y-3">
                  <label className="text-sm text-muted-foreground block">
                    {t('map.scopeLabel') || 'Location scope'}
                  </label>
                  <Tabs
                    value={filters.scopeMode}
                    onValueChange={(v) => updateFilter('scopeMode', v)}
                    className="w-full"
                  >
                    <TabsList className="grid w-full grid-cols-3" data-testid="scope-tabs">
                      <TabsTrigger value="country" data-testid="scope-tab-country">
                        {t('map.scopeCountry') || 'Country'}
                      </TabsTrigger>
                      <TabsTrigger value="city" data-testid="scope-tab-city">
                        {t('map.scopeCity') || 'City'}
                      </TabsTrigger>
                      <TabsTrigger value="distance" data-testid="scope-tab-distance">
                        {t('map.scopeDistance') || 'Distance'}
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>

                  {/* CITY mode — text input that geocodes on enter/blur */}
                  {filters.scopeMode === 'city' && (
                    <div className="space-y-2">
                      <Input
                        placeholder={t('map.cityPlaceholder') || 'City or region'}
                        value={filters.city}
                        onChange={(e) => updateFilter('city', e.target.value)}
                        onBlur={async (e) => {
                          // Read the live DOM value to avoid stale-closure
                          // (onChange's setFilters hasn't re-rendered yet
                          // when the user types-then-blurs in one motion).
                          const q = (e.target.value || '').trim();
                          if (!q) return;
                          try {
                            const r = await axios.post(`${BACKEND_URL}/api/map/geocode`, {
                              query: q, country: filters.country || undefined, language,
                            });
                            setFilters((p) => ({
                              ...p,
                              lat: r.data.lat,
                              lng: r.data.lng,
                              cityLabel: r.data.normalized_address,
                            }));
                            setMapCenter({ lat: r.data.lat, lng: r.data.lng });
                            setMapZoom(11);
                          } catch (_) { /* silent — user can keep typing */ }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        data-testid="city-filter"
                      />
                      <Select
                        value={String(filters.area_km)}
                        onValueChange={(v) => updateFilter('area_km', parseInt(v, 10))}
                      >
                        <SelectTrigger data-testid="area-size">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30">{t('map.areaNearby') || 'Nearby'}</SelectItem>
                          <SelectItem value="50">50 km</SelectItem>
                          <SelectItem value="100">100 km</SelectItem>
                          <SelectItem value="250">250 km</SelectItem>
                        </SelectContent>
                      </Select>
                      {filters.lat && filters.lng && filters.cityLabel && (
                        <div className="text-xs text-emerald-700 truncate" data-testid="city-resolved">
                          ✓ {filters.cityLabel}
                        </div>
                      )}
                    </div>
                  )}

                  {/* DISTANCE mode — slider + numeric + preset chips */}
                  {filters.scopeMode === 'distance' && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Slider
                          value={[Math.min(filters.radius_km, 10000)]}
                          min={0}
                          max={10000}
                          step={5}
                          onValueChange={(v) => updateFilter('radius_km', v[0])}
                          className="flex-1"
                          data-testid="distance-slider"
                        />
                        <Input
                          type="number"
                          min="0"
                          max="10000"
                          value={filters.radius_km}
                          onChange={(e) => {
                            const n = Math.max(0, Math.min(10000, parseInt(e.target.value || '0', 10)));
                            updateFilter('radius_km', n);
                          }}
                          className="w-24"
                          data-testid="distance-input"
                        />
                        <span className="text-xs text-slate-500">km</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {[25, 50, 100, 250, 500, 1000, 2500, 5000, 10000].map((km) => (
                          <Button
                            key={km}
                            variant={filters.radius_km === km ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateFilter('radius_km', km)}
                            className={`px-2.5 py-1 h-7 text-xs ${
                              filters.radius_km === km ? 'bg-orange-500 hover:bg-orange-600' : ''
                            }`}
                            data-testid={`distance-preset-${km}`}
                          >
                            {km < 1000 ? `${km} km` : `${km / 1000}k km`}
                          </Button>
                        ))}
                      </div>
                      {!filters.lat || !filters.lng ? (
                        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-2 flex items-center justify-between">
                          <span className="text-xs text-slate-600" data-testid="distance-need-center">
                            {t('map.scopeNeedCenter') || 'Pick a city or use your location.'}
                          </span>
                          <Button
                            size="sm" variant="outline"
                            onClick={requestLocation}
                            data-testid="use-location-btn"
                          >
                            <MapPin className="h-3.5 w-3.5 mr-1" />
                            {t('map.useMyLocation')}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline" size="sm" className="w-full"
                          onClick={requestLocation}
                          data-testid="use-location-btn"
                        >
                          <MapPin className="h-3.5 w-3.5 mr-1" />
                          {t('map.useMyLocation')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Sort Dropdown - Nearest only for now */}
                {/* NOTE: "Rated" sort hidden until rating system is implemented */}
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">
                    {t('map.sortBy') || 'Sorteeri'}
                  </label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-full" data-testid="sort-dropdown">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nearest">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3 h-3" />
                          {t('map.sortNearest') || 'Lähimad enne'}
                        </div>
                      </SelectItem>
                      {/* Rated sort will be added when rating system is implemented */}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Share Button */}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleShareMap}
                  data-testid="share-map-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  {t('map.shareMap') || 'Jaga seda kaarti'}
                </Button>
              </div>
              
              {/* Results List */}
              <div className="flex-1 overflow-hidden">
                <div className="p-4 border-b bg-muted/50 flex items-center gap-2">
                  <LayerIcon className="w-4 h-4" style={{ color: layerColor }} />
                  <span className="text-sm text-muted-foreground">
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                    ) : (
                      `${items.length} ${t('map.resultsFound') || 'results found'}`
                    )}
                  </span>
                </div>
                
                <ScrollArea className="h-[calc(100vh-450px)] lg:h-[calc(100vh-370px)]">
                  <div className="p-4 space-y-3">
                    {loading ? (
                      Array(3).fill(0).map((_, i) => (
                        <Card key={i} className="p-4">
                          <Skeleton className="h-4 w-3/4 mb-2" />
                          <Skeleton className="h-3 w-1/2 mb-3" />
                          <Skeleton className="h-8 w-full" />
                        </Card>
                      ))
                    ) : items.length === 0 ? (
                      <EmptyStateWithCTA 
                        activeLayer={activeLayer}
                        availableLayers={availableLayers}
                        layerColor={layerColor}
                        LayerIcon={LayerIcon}
                        t={t}
                        language={language}
                      />
                    ) : (
                      items.map(item => (
                        <ItemCard
                          key={item.id}
                          item={item}
                          isSelected={selectedItem?.id === item.id}
                          onClick={() => {
                            setSelectedItem(item);
                            setMapCenter({ lat: item.lat, lng: item.lng });
                            setMapZoom(14);
                          }}
                          t={t}
                          layerColor={layerColor}
                        />
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>
          )}
        </aside>
        
        {/* Map */}
        <div className="flex-1 relative min-h-[400px] lg:min-h-0">
          <MapContainer
            center={[mapCenter.lat, mapCenter.lng]}
            zoom={mapZoom}
            className="h-full w-full z-0"
            style={{ minHeight: '400px' }}
            data-testid="live-map"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* Skip auto-recenter when logistics route preview is active —
                LogisticsRouteLayer manages its own fitBounds. */}
            {searchParams.get('mode') !== 'logistics' && (
              <SetViewOnChange center={mapCenter} zoom={mapZoom} />
            )}
            <MapEventHandler onBoundsChange={handleBoundsChange} />
            <PopupClickTracker />

            {/* Logistics route preview overlay — driven by ?mode=logistics&... */}
            {searchParams.get('mode') === 'logistics' && (
              <LogisticsRouteLayer
                params={searchParams}
                onRouteUpdate={handleLogisticsRouteUpdate}
              />
            )}

            {/* Clustered Markers */}
            <MarkerClusterGroup
              items={items}
              onMarkerClick={(item) => {
                setSelectedItem(item);
                // Clean v2 funnel tracker (deduped, structured payload).
                trackMapEvent('pin_click', {
                  label: item.title || item.name || item.id,
                  providerId: item.id,
                });
              }}
              t={t}
              layerColor={layerColor}
              activeLayer={activeLayer}
            />
          </MapContainer>

          {/* Logistics route info card — sibling of MapContainer for proper
              HTML rendering above the map (Leaflet child divs would render
              inside the SVG/marker layer and be invisible). */}
          {searchParams.get('mode') === 'logistics' && (
            <LogisticsRouteInfoCard
              route={logisticsRoute}
              originCity={searchParams.get('originCity')}
              destCity={searchParams.get('destCity')}
              t={t}
            />
          )}

          {/* Upsell Banner for Basic Providers */}
          <MapUpsellBanner 
            onBoostClick={() => {
              window.location.href = '/provider/dashboard?upgrade=boost';
            }}
          />
          
          {/* Map Legend */}
          <div className="absolute bottom-4 right-4 bg-card/90 backdrop-blur-sm p-3 rounded-lg shadow-lg z-10">
            <div className="text-xs font-medium mb-2">{t('map.legend')}</div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 rounded-full" style={{ background: TIER_COLORS.premium }} />
                <span>Premium</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 rounded-full" style={{ background: TIER_COLORS.boost }} />
                <span>Boost</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 rounded-full" style={{ background: TIER_COLORS.basic }} />
                <span>Basic</span>
              </div>
            </div>
          </div>
        </div>
        </div>
      </main>
      
      <Footer />
      
      {/* Custom styles for markers and clusters */}
      <style>{`
        .custom-marker {
          background: transparent;
          border: none;
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        /* Cluster styles */
        .custom-cluster {
          background: transparent;
          border: none;
        }
        .custom-cluster .cluster-inner {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          color: white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          border: 3px solid white;
        }
        .marker-cluster-small .cluster-inner {
          background: #3B82F6;
        }
        .marker-cluster-medium .cluster-inner {
          background: #22C55E;
          width: 45px;
          height: 45px;
          font-size: 15px;
        }
        .marker-cluster-large .cluster-inner {
          background: #FFD700;
          width: 50px;
          height: 50px;
          font-size: 16px;
          color: #333;
        }
        
        /* Override leaflet default cluster styles */
        .leaflet-marker-icon.marker-cluster {
          background: transparent !important;
        }
        .marker-cluster div {
          background: transparent !important;
        }
        
        /* Popup link styles */
        .leaflet-popup-content a {
          text-decoration: none;
        }
      `}</style>
    </div>
  );
}
